import { type QueryClient } from '@tanstack/react-query';

import {
  getLedgerAccountUniquenessQueryOptions,
  zodValidateLedgerAccountUniquenessQueryParamsSchema,
} from '~/data/ledgerAccount';

import { i18n } from '~/utils';

export type LedgerAccountDuplicateCheckFieldName =
  | 'companyId'
  | 'number'
  | 'validFrom';

type FieldErrors = Record<
  LedgerAccountDuplicateCheckFieldName,
  { message: string } | undefined
>;

export type LedgerAccountDuplicateCheckContext = {
  changedField: LedgerAccountDuplicateCheckFieldName | undefined;
};

/**
 * Runs the server-side check for an existing ledger account with the same
 * number/company/valid-from combination and reports the result as a
 * form-level error (TanStack Form's `fields` error map), targeted at
 * whichever of the three fields the caller says was just edited.
 *
 * @param queryClient - The app's query client, used to fetch through the shared query cache.
 * @param params - The current values of the three fields the uniqueness check depends on.
 * @param context - Targeting info; see {@link LedgerAccountDuplicateCheckContext}.
 * @returns A `fields` error map with at most one message set, or all three explicitly cleared.
 */
export async function runLedgerAccountDuplicateCheck(
  queryClient: QueryClient,
  params: {
    companyId: string;
    number: string;
    validFrom: string;
  },
  { changedField }: LedgerAccountDuplicateCheckContext,
) {
  const clearedFields: FieldErrors = {
    companyId: undefined,
    number: undefined,
    validFrom: undefined,
  };

  // Nothing to check before the user has touched one of the three fields --
  // otherwise editing an unrelated field (name, cost type, account type)
  // would retrigger this form-level validator for no reason.
  if (!changedField) {
    return { fields: clearedFields };
  }

  const parsedParams =
    zodValidateLedgerAccountUniquenessQueryParamsSchema.safeParse(params);

  if (!parsedParams.success) {
    return { fields: clearedFields };
  }

  const { isDuplicate } = await queryClient.fetchQuery(
    getLedgerAccountUniquenessQueryOptions({
      queryParams: parsedParams.data,
    }),
  );

  if (!isDuplicate) {
    return { fields: clearedFields };
  }

  return {
    fields: {
      ...clearedFields,
      [changedField]: {
        message: i18n.t(($) => $.form.errors.duplicate, {
          ns: 'ledgerAccounts',
        }),
      },
    },
  };
}
